import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { setOnUnauthorized } from './api/client';
import AppLayout from './components/layout/AppLayout';
import ProtectedRoute from './components/routing/ProtectedRoute';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import Dashboard from './pages/dashboard/Dashboard';
import Users from './pages/users/Users';
import Roles from './pages/users/Roles';
import ChartOfAccounts from './pages/accounts/ChartOfAccounts';
import AccountStructure from './pages/accounts/AccountStructure';
import Parties from './pages/parties/Parties';
import PartyForm from './pages/parties/PartyForm';
import Suppliers from './pages/suppliers/Suppliers';
import SupplierForm from './pages/suppliers/SupplierForm';
import Vouchers from './pages/vouchers/Vouchers';
import VoucherForm from './pages/vouchers/VoucherForm';
import VoucherDetail from './pages/vouchers/VoucherDetail';
import Invoices from './pages/invoices/Invoices';
import InvoiceForm from './pages/invoices/InvoiceForm';
import InvoiceDetail from './pages/invoices/InvoiceDetail';
import DebitNotes from './pages/invoices/DebitNotes';
import DebitNoteForm from './pages/invoices/DebitNoteForm';
import Payments from './pages/invoices/Payments';
import Reports from './pages/reports/Reports';
import ReportPage from './pages/reports/ReportPage';
import Ledger from './pages/reports/Ledger';
import AccountsBalances from './pages/reports/AccountsBalances';
import TrialBalance from './pages/reports/TrialBalance';
import IncomeStatement from './pages/reports/IncomeStatement';
import BalanceSheet from './pages/reports/BalanceSheet';
import Employees from './pages/employees/Employees';
import EmployeeForm from './pages/employees/EmployeeForm';
import EmployeeDetail from './pages/employees/EmployeeDetail';
import Machines from './pages/machines/Machines';
import MachineDetail from './pages/machines/MachineDetail';
import MachineArticles from './pages/machines/MachineArticles';
import Stitches from './pages/machines/Stitches';
import Attendance from './pages/hr/Attendance';
import Departments from './pages/hr/Departments';
import Shifts from './pages/hr/Shifts';
import ShiftForm from './pages/hr/ShiftForm';
import Advances from './pages/hr/Advances';
import AdvanceForm from './pages/hr/AdvanceForm';
import Leaves from './pages/hr/Leaves';
import Payroll from './pages/payroll/Payroll';
import PayrollDetail from './pages/payroll/PayrollDetail';
import Gatepasses from './pages/inventory/Gatepasses';
import GatepassForm from './pages/inventory/GatepassForm';
import GatepassDetail from './pages/inventory/GatepassDetail';
import Inventory from './pages/inventory/Inventory';
import InventoryItems from './pages/inventory/InventoryItems';
import Units from './pages/inventory/Units';
import SettingsPage from './pages/settings/SettingsPage';
import ActivityLogs from './pages/activity/ActivityLogs';
import AccountPermissions from './pages/permissions/AccountPermissions';
import Forbidden from './pages/errors/Forbidden';
import NotFound from './pages/errors/NotFound';

export default function App() {
  const auth = useAuth();

  React.useEffect(() => {
    setOnUnauthorized(() => {
      window.location.href = '/login';
    });
  }, []);

  if (auth.loading) {
    return (
      <div className="loader">
        <div className="spinner" />
        <div>Loading Ahmed Corporation ERP...</div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={auth.isAuthenticated ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/register" element={auth.isAuthenticated ? <Navigate to="/" replace /> : <Register />} />

      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/forbidden" element={<Forbidden />} />

          <Route path="/users" element={<Users />} />
          <Route path="/roles" element={<Roles />} />

          <Route path="/accounts/coa" element={<ChartOfAccounts />} />
          <Route path="/accounts/structure" element={<AccountStructure />} />

          <Route path="/parties" element={<Parties />} />
          <Route path="/parties/new" element={<PartyForm />} />
          <Route path="/parties/:id/edit" element={<PartyForm />} />
          <Route path="/suppliers" element={<Suppliers />} />
          <Route path="/suppliers/new" element={<SupplierForm />} />
          <Route path="/suppliers/:id/edit" element={<SupplierForm />} />

          <Route path="/vouchers" element={<Vouchers />} />
          <Route path="/vouchers/new" element={<VoucherForm />} />
          <Route path="/vouchers/:id/edit" element={<VoucherForm />} />
          <Route path="/vouchers/:id" element={<VoucherDetail />} />

          <Route path="/invoices" element={<Invoices />} />
          <Route path="/invoices/new" element={<InvoiceForm />} />
          <Route path="/invoices/purchase" element={<Invoices />} />
          <Route path="/invoices/purchase/new" element={<InvoiceForm />} />
          <Route path="/invoices/:id/edit" element={<InvoiceForm />} />
          <Route path="/invoices/:id" element={<InvoiceDetail />} />
          <Route path="/debit-notes" element={<DebitNotes />} />
          <Route path="/debit-notes/new" element={<DebitNoteForm />} />
          <Route path="/debit-notes/:id/edit" element={<DebitNoteForm />} />
          <Route path="/payments" element={<Payments />} />

          <Route path="/employees" element={<Employees />} />
          <Route path="/employees/new" element={<EmployeeForm />} />
          <Route path="/employees/:id/edit" element={<EmployeeForm />} />
          <Route path="/employees/:id" element={<EmployeeDetail />} />

          <Route path="/machines" element={<Machines />} />
          <Route path="/machines/:id" element={<MachineDetail />} />
          <Route path="/machine-articles" element={<MachineArticles />} />
          <Route path="/stitches" element={<Stitches />} />

          <Route path="/attendance" element={<Attendance />} />
          <Route path="/departments" element={<Departments />} />
          <Route path="/shifts" element={<Shifts />} />
          <Route path="/shifts/new" element={<ShiftForm />} />
          <Route path="/shifts/:id/edit" element={<ShiftForm />} />
          <Route path="/advances" element={<Advances />} />
          <Route path="/advances/new" element={<AdvanceForm />} />
          <Route path="/leaves" element={<Leaves />} />

          <Route path="/payroll" element={<Payroll />} />
          <Route path="/payroll/:id" element={<PayrollDetail />} />

          <Route path="/gatepasses" element={<Gatepasses />} />
          <Route path="/gatepasses/new" element={<GatepassForm />} />
          <Route path="/gatepasses/:id" element={<GatepassDetail />} />
          <Route path="/gatepasses/:id/edit" element={<GatepassForm />} />
          <Route path="/inventory/items" element={<InventoryItems />} />
          <Route path="/inventory/units" element={<Units />} />
          <Route path="/inventory/stock" element={<Inventory />} />

          <Route path="/reports" element={<Reports />} />
          <Route path="/reports/ledger" element={<Ledger />} />
          <Route path="/reports/accounts-balances" element={<AccountsBalances />} />
          <Route path="/reports/trial-balance" element={<TrialBalance />} />
          <Route path="/reports/income-statement" element={<IncomeStatement />} />
          <Route path="/reports/balance-sheet" element={<BalanceSheet />} />
          <Route path="/reports/:reportKey" element={<ReportPage />} />

          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/account-permissions" element={<AccountPermissions />} />
          <Route path="/activity-logs" element={<ActivityLogs />} />
        </Route>
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}